import { DOMAIN_MAX, type PositionId, type Selection } from "./model";

export type CompetencyToggleResult = {
  nextSelection: Selection;
  selected: boolean;
  selectedCountAfter: number;
  requiredCountAfter: number;
};

export type RequiredToggleResult = {
  nextSelection: Selection;
  required: boolean;
  selectedCount: number;
  requiredCountAfter: number;
};

export type DomainToggleResult = {
  nextSelection: Selection;
  selected: boolean;
  domainCountAfter: number;
} | null;

export type ExcludeToggleResult = {
  nextSelection: Selection;
  selected: boolean;
  excludeCountAfter: number;
};

export function selectPosition(current: Selection, positionId: PositionId): Selection {
  return current.positionId === positionId ? current : { ...current, positionId, competencyIds: [], requiredIds: [] };
}

export function toggleCompetencySelection(current: Selection, competencyId: string): CompetencyToggleResult {
  const alreadySelected = current.competencyIds.includes(competencyId);
  const nextCompetencyIds = alreadySelected
    ? current.competencyIds.filter((id) => id !== competencyId)
    : [...current.competencyIds, competencyId];
  const nextRequiredIds = alreadySelected ? current.requiredIds.filter((id) => id !== competencyId) : current.requiredIds;

  return {
    nextSelection: {
      ...current,
      competencyIds: nextCompetencyIds,
      requiredIds: nextRequiredIds,
    },
    selected: !alreadySelected,
    selectedCountAfter: nextCompetencyIds.length,
    requiredCountAfter: nextRequiredIds.length,
  };
}

export function toggleRequiredSelection(current: Selection, competencyId: string): RequiredToggleResult | null {
  if (!current.competencyIds.includes(competencyId)) return null;

  const alreadyRequired = current.requiredIds.includes(competencyId);
  const nextRequiredIds = alreadyRequired
    ? current.requiredIds.filter((id) => id !== competencyId)
    : [...current.requiredIds, competencyId];

  return {
    nextSelection: { ...current, requiredIds: nextRequiredIds },
    required: !alreadyRequired,
    selectedCount: current.competencyIds.length,
    requiredCountAfter: nextRequiredIds.length,
  };
}

export function toggleDomainSelection(current: Selection, domainId: string): DomainToggleResult {
  const alreadySelected = current.domainIds.includes(domainId);
  if (!alreadySelected && current.domainIds.length >= DOMAIN_MAX) return null;

  const nextDomainIds = alreadySelected ? current.domainIds.filter((id) => id !== domainId) : [...current.domainIds, domainId];

  return {
    nextSelection: { ...current, domainIds: nextDomainIds },
    selected: !alreadySelected,
    domainCountAfter: nextDomainIds.length,
  };
}

export function toggleExcludeSelection(current: Selection, excludeId: string): ExcludeToggleResult {
  const alreadySelected = current.excludeIds.includes(excludeId);
  const nextExcludeIds = alreadySelected
    ? current.excludeIds.filter((id) => id !== excludeId)
    : [...current.excludeIds, excludeId];

  return {
    nextSelection: { ...current, excludeIds: nextExcludeIds },
    selected: !alreadySelected,
    excludeCountAfter: nextExcludeIds.length,
  };
}
